import {
  LiveNationCheckoutTermsV2,
  LiveNationRegistrationTerms,
  LiveNationAdditionalDetailsDisclaimerTC,
} from "../tc_helper_livenation";
import { routes } from "@/global/routes";
import { faqs, howItWorks } from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import FaviconImage from "public/images/livenation.png";
import BackgroundImage from "public/images/utahfirst/background.jpg";

import type { TeamConfig } from "../types";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/LiveNation%20Assets/Logos/NATL_VenueLogos_Color_Toyota%20Pavilion%20at%20Concord%20.png";
const staticSeatMapImage =
  "https://ss-leagues.s3.amazonaws.com/LiveNation Assets/Maps/Toyota_Concord_Map.jpg";
const bannerImage =
  "https://ss-leagues.s3.amazonaws.com/LiveNation Assets/Toyota Pavilion at Concord/ToyotaPavilionConcord_1486x148.png";

const config: TeamConfig = {
  teamSchema: "toyotapavilion_premium",
  browserTitle: "Toyota Pavilion at Concord Premium",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Live Nation",

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    staticSeatMapImage,

    isShowAvailableSeatsButtonHidden: true,
  },

  // info fields configuration for tracking UTM parameters
  infoFieldsConfig: {
    utm_source: {
      infoField: 3,
      name: "utm_source",
    },
    utm_medium: {
      infoField: 4,
      name: "utm_medium",
    },
    utm_campaign: {
      infoField: 5,
      name: "utm_campaign",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    isPerksEnabled: true,
    defaultSeatCount: 2,
    isCheckoutAdditionalDetailsEnabled: true,
    hideAllUpcomingGames: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    beaconId: "",
    attrkPixelId: "",
    googleAnalyticsId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/toyotapavilion_premium/events_home",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/lnconcerts",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage: "",
    faviconImage: FaviconImage.src,
    findGamesBannerImage: bannerImage,
    authBackgroundImage: BackgroundImage.src,
    logoImage: logoImage + "?width=126&height=66",
    checkoutStatusPageImage: BackgroundImage.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
      standardEventCard: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    hideFeaturedGames: true,

    inventoryCardSettings: {
      hideFBPackageLineItem: true,
    },

    viewTicketsUrl: "https://am.ticketmaster.com/lnconcerts",

    perks: {
      hideAddAllButton: true,
    },

    checkoutPage: {
      checkoutForm: {
        hideCateringAndTicketContact: true,
        optionalAdditionalDetailsContractTitle: true,
        optionalAdditionalDetailsCompanyName: true,
      },
    },
  },

  colors: {
    primary: "#000000",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <LiveNationCheckoutTermsV2 />,
    registrationTermsAndCondition: <LiveNationRegistrationTerms />,
    additionalDetailsDisclaimerTC: <LiveNationAdditionalDetailsDisclaimerTC />,
    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all events in your
          sessions. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarGameText: "event",
      purchaseSeatTabTitle: "Choose Events",
      confirmEventSelectionLossTitle: "Select Your Series",
      bottomBarEventsDescription: "Event",
      findGamesTitle: "Featured Events",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
      noSessionsAvailable: "No events available",
      cancelCartEventLabel: "events",
      changeSeatsButtonLabel: "Edit Selection",
      chooseAddOnsTabTitle: "Choose Upgrades",
      addOnsLabel: "Upgrades",
    },

    purchaseHistory: {
      noUpcomingGames: "No purchase history available.",
      purchaseHistoryGameLabel: "Event",
    },

    autoSeatSelectionAlertModal: {
      description: {
        manualSelectionAllowed: (
          <>
            Would you like to automatically select similar seats for your
            remaining events or choose seats manually?
            <br />
            <br />* Please Note: Some seating options presented may be shared
            spaces. If the seats you select do not make up an entire box or
            suite, the remaining seats may be sold to other parties. This will
            be more likely when purchasing seats in pairs.
          </>
        ),
      },
    },

    opponentSelectorTitle: "Artist",

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Toyota Pavilion Premium Seating confirmation was successful! A representative will be in touch shortly to follow up with any questions you may have.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Account Manager to continue with seamless access.",
  },
};

export default config;
